import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import { createPortal } from "react-dom";
import "./dayViewModal.css";
import "../../AdminPages/CalendarPage/calendarPage.css";
import "../DayModal/dayModal.css";
import "../../../../utils/tentativeShimmer.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import HearingIcon from "@mui/icons-material/Hearing";
import EventDetailPopup from "../EventDetailPopup/EventDetailPopup";
import EventFilterToggle from "../EventFilterToggle/EventFilterToggle";
import AllDayEventStrip from "../AllDayEventStrip/AllDayEventStrip";
import {
  EVENT_FILTER_PERSONAL,
  applyEventFilter,
  isAllDayItem,
  allDayItemsForDay,
  mergeSummaryForFilter,
} from "../../../../utils/calendarPersonalEvents";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import AIAssignmentPanel from "../AIAssignmentPanel/AIAssignmentPanel";
import EditEventModal from "../EditEventModal/EditEventModal";
import CreateEventModal from "../CreateEventModal/CreateEventModal";
import ViewClientInvoiceModal from "../ViewClientInvoiceModal/ViewClientInvoiceModal";
import ViewClientQuoteModal from "../ViewClientQuoteModal/ViewClientQuoteModal";
import JobFormReviewModal from "../JobFormReviewModal/JobFormReviewModal";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { computeLaneLayout, getBlockLabelPrefix, getBlockLabelName, personalEventColor } from "../../../../utils/calendarLaneLayout";
import { getCalendarJobCategory } from "../../../../utils/calendarJobCategory";
import { getFinisherRange } from "../../../../utils/calendarSplitBetween";
import { useCalendarHoverHighlight } from "../../../../utils/useCalendarHoverHighlight";
import "../../../../utils/vacationStripes.css";
import { splitEventForDay } from "../../../../utils/calendarEventSegments";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { useLazyQuery } from "@apollo/client";
import { GET_ZONE_WEATHER_FOR_RANGE } from "../../../../utils/GraphQL/queries";
import WeatherChip from "../../../../components/WeatherChip/WeatherChip";
import { buildWeatherLookup, weatherForJob } from "../../../../utils/weatherZoneJoin";
import MultiDayBanner from "../shared/MultiDayBanner/MultiDayBanner";
import CurrentTimeLine from "../CurrentTimeLine/CurrentTimeLine";

// Grid constants — mirror WeekModal so a day column renders identically
// to one column of the week view. Renderable range stays 24h so events
// that land outside the 6a-10p default window still draw and scroll into
// view; the initial scroll lands on 6a (or the first job of the day).
const HOUR_HEIGHT = 30;
const GRID_START_HOUR = 6;
const GRID_HOURS = 24;
const TOTAL_GRID_HEIGHT = GRID_HOURS * HOUR_HEIGHT;
const HOUR_LABEL_WIDTH = 50;
const DEFAULT_SCROLL_HOUR = 6;

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i).toLocaleDateString("en-US", { month: "long" })
);
// Weather (weather-by-zone) is cached per selected-day for 45 min and is
// intentionally DECOUPLED from the calendar_update SSE refetch — a job moving
// doesn't change the forecast, so weather must not refetch on every SSE.
// Mirrors WeekModal's WEATHER_TTL_MS.
const WEATHER_TTL_MS = 45 * 60 * 1000;

const formatHourLabel = (hour) => {
  const h = hour % 24;
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  if (h < 12) return `${h}a`;
  return `${h - 12}p`;
};

// Returns the displayed dollar amount for a calendar event block — the
// invoice total with tip line items stripped, falling back to the raw
// quote total for quote-only events. Refinishers + admins manually verify
// pay by reading this number × payPercentage; tips go to a separate
// full-pay bucket server-side, so leaving them in the displayed total
// over-counts the manual math. Returns null on $0-net invoices so the
// pill suppresses itself (semantic shift from "$0.00" prior — see
// WEB_CHANGELOG entry 2026-05-08).
const getJobAmountStr = (item) => {
  if (item.invoice?.invoiceItems) {
    const totalCents = parseInt(item.invoice.invoiceTotal) || 0;
    const tipCents = (item.invoice.invoiceItems || []).reduce((sum, it) => {
      if ((it.itemName || "").trim().toLowerCase() !== "tip") return sum;
      return sum + (parseInt(it.price) || 0) * (parseInt(it.quantity) || 1);
    }, 0);
    const displayCents = Math.max(totalCents - tipCents, 0);
    return displayCents > 0 ? priceStrFormatter(String(displayCents)) : null;
  }
  // Quote fallback: quotes never carry tips so subtraction is a no-op.
  const cents = item.quote?.quoteTotal;
  return cents ? priceStrFormatter(cents) : null;
};

// Min/Max finisher chip (bottom-left of event blocks): `getFinisherRange` is
// imported from utils/calendarSplitBetween.js — the doc's splitBetween, or a
// tentative placeholder's own number (2026-10-29).

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/**
 * Single-day view with a mini-month navigator on the left. The time grid
 * is a verbatim port of one WeekModal column (same constants, same lane
 * layout, same multi-day chevron banner, same user-color backgrounds).
 *
 * getScheduleRange on a single day works out of the box: the server uses
 * overlap semantics on parent startMs/endMs, so a Mon/Tue/Thu multi-day
 * job is returned for any single-day query within Mon→Thu. splitEventForDay
 * then drops Wednesday naturally (no matching workDays entry) and attaches
 * isFirstDay / isLastDay for the chevron banner.
 */
const DayViewModal = ({
  open,
  onClose,
  currentMonth,
  setCurrentMonth,
  initialDay,
  getScheduleRange,
  getMonthSummary,
  onSwitchToWeek,
  onSwitchToMonth,
  // Personal ("SIX") events, 2026-09-11 — see WeekModal for the contract.
  isAdmin = false,
  eventFilter = "all",
  onEventFilterChange,
}) => {
  const [selectedDay, setSelectedDay] = useState(null);
  // Mini-month can be navigated independently of selectedDay. Picking a
  // day inside the mini updates selectedDay but does not force the mini
  // month back — users can browse ahead without committing to a day.
  const [miniMonth, setMiniMonth] = useState(null);
  const [items, setItems] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createEventStartMs, setCreateEventStartMs] = useState("");
  const [createEventEndMs, setCreateEventEndMs] = useState("");
  // "job" from Create / grid click; "personal" from the SIX button.
  const [createEventType, setCreateEventType] = useState("job");
  const [hoveredHour, setHoveredHour] = useState(null);
  // Chain-aware hover spotlight (dim others + light up chain).
  const { onEnter: onHoverEnter, onLeave: onHoverLeave, styleFor: hoverStyleFor, isHoverActive } = useCalendarHoverHighlight();
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewInvoiceId, setReviewInvoiceId] = useState(null);

  const [pickerOpen, setPickerOpen] = useState(null); // null | 'month' | 'year'
  const pickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  const gridBodyRef = useRef(null);
  const prevOpenRef = useRef(false);

  // --- Weather-by-zone state (decoupled from the schedule/SSE refetch) ---
  // zoneWeather: getZoneWeatherForRange result for the selected day's window.
  // weatherCacheRef: { [dayKey]: { data, ts } } — in-memory TTL cache so
  // navigating away/back (or an SSE schedule refetch) reuses the forecast
  // instead of re-hitting the network. Failure is swallowed (no chip renders).
  const [zoneWeather, setZoneWeather] = useState(null);
  const weatherCacheRef = useRef({});
  const [getZoneWeather] = useLazyQuery(GET_ZONE_WEATHER_FOR_RANGE);

  // Init selectedDay + miniMonth when the modal transitions to open.
  // Precedence:
  //   1. initialDay — set by CalendarPage when a month-grid day was clicked;
  //      lands DayViewModal on that exact date so the consolidated detail
  //      view replaces the old DayModal entry path.
  //   2. currentMonth matches today's month → today (in-session "View Day"
  //      from the current week, or the parent's render-phase reset on each
  //      calendar open).
  //   3. Otherwise → 1st of currentMonth (in-session jump after the user
  //      navigated to a different month in another view).
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;

    let refDate;
    if (initialDay) {
      refDate = startOfDay(initialDay);
    } else {
      const now = new Date();
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      refDate =
        now.getFullYear() === year && now.getMonth() === month
          ? startOfDay(now)
          : new Date(year, month, 1);
    }

    setSelectedDay(refDate);
    setMiniMonth(new Date(refDate.getFullYear(), refDate.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch the day's events whenever the selected day changes.
  useEffect(() => {
    if (open && selectedDay) {
      fetchDayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDay]);

  // Fetch mini-month summaries (for the colored dots) whenever the mini
  // month's displayed month changes.
  useEffect(() => {
    if (open && miniMonth) {
      fetchMiniMonthData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, miniMonth]);

  // Fetch weather ONCE per selected day (cached 45 min). Separate from
  // fetchDayData / the SSE refetch so a job moving never re-pulls the forecast.
  // A ±1-day window is requested (matching WeekModal's robustness) so a job
  // near a day boundary still joins. On a cache hit within TTL we reuse the
  // stored result; otherwise we fetch and store it. A weather failure is
  // swallowed (chip just doesn't render) — it never blocks or errors the grid.
  useEffect(() => {
    if (!open || !selectedDay) return;
    const dayKey = `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`;
    const cached = weatherCacheRef.current[dayKey];
    if (cached && Date.now() - cached.ts < WEATHER_TTL_MS) {
      setZoneWeather(cached.data);
      return;
    }
    const winStart = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate() - 1
    );
    winStart.setHours(0, 0, 0, 0);
    const winEnd = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate() + 1
    );
    winEnd.setHours(23, 59, 59, 999);
    const startMs = winStart.getTime().toString();
    const endMs = winEnd.getTime().toString();

    let cancelled = false;
    (async () => {
      try {
        const { data } = await getZoneWeather({
          variables: { startMs, endMs },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const result = data?.getZoneWeatherForRange || [];
        weatherCacheRef.current[dayKey] = { data: result, ts: Date.now() };
        setZoneWeather(result);
      } catch (err) {
        // Non-fatal: degrade to no chips, never surface an error in the grid.
        console.error("Error fetching zone weather:", err);
        if (!cancelled) setZoneWeather(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDay]);

  const fetchDayData = async () => {
    if (!selectedDay) return;
    setLoading(true);
    setError(null);
    setSelectedItem(null);

    const rangeStart = startOfDay(selectedDay).getTime().toString();
    const rangeEnd = endOfDay(selectedDay).getTime().toString();

    try {
      const { data } = await getScheduleRange({
        variables: { rangeStart, rangeEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      const results = (data?.getScheduleRange || []).filter(
        (r) => r?.calendarItem?.startMs
      );
      results.sort(
        (a, b) =>
          parseInt(a.calendarItem.startMs) - parseInt(b.calendarItem.startMs)
      );
      setItems(results);
    } catch (err) {
      console.error("Error fetching day schedule:", err);
      setError("Error loading schedule");
    }
    setLoading(false);
  };

  const fetchMiniMonthData = async () => {
    if (!miniMonth) return;
    const monthStart = new Date(
      miniMonth.getFullYear(),
      miniMonth.getMonth(),
      1
    ).getTime().toString();
    const monthEnd = new Date(
      miniMonth.getFullYear(),
      miniMonth.getMonth() + 1,
      0,
      23,
      59,
      59
    ).getTime().toString();

    try {
      const { data } = await getMonthSummary({
        variables: { monthStart, monthEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      setSummaries(data?.getScheduleMonthSummary || []);
    } catch (err) {
      console.error("Error fetching mini-month summary:", err);
    }
  };

  // SSE: silent refetch of both streams, preserving the open selection.
  const silentRefetch = useCallback(async () => {
    if (!open || !selectedDay || !miniMonth) return;

    // Day events
    const rangeStart = startOfDay(selectedDay).getTime().toString();
    const rangeEnd = endOfDay(selectedDay).getTime().toString();
    try {
      const { data } = await getScheduleRange({
        variables: { rangeStart, rangeEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      const results = (data?.getScheduleRange || []).filter(
        (r) => r?.calendarItem?.startMs
      );
      results.sort(
        (a, b) =>
          parseInt(a.calendarItem.startMs) - parseInt(b.calendarItem.startMs)
      );
      setItems(results);
      setSelectedItem((prev) => {
        if (!prev) return null;
        return (
          results.find(
            (r) => r.calendarItem.id === prev.calendarItem.id
          ) || null
        );
      });
    } catch (err) {
      console.error("SSE day refetch error:", err);
    }

    // Mini-month dots
    try {
      const monthStart = new Date(
        miniMonth.getFullYear(),
        miniMonth.getMonth(),
        1
      ).getTime().toString();
      const monthEnd = new Date(
        miniMonth.getFullYear(),
        miniMonth.getMonth() + 1,
        0,
        23,
        59,
        59
      ).getTime().toString();
      const { data } = await getMonthSummary({
        variables: { monthStart, monthEnd, onlyGetInvoiced: false, includePersonal: isAdmin },
        fetchPolicy: "network-only",
      });
      setSummaries(data?.getScheduleMonthSummary || []);
    } catch (err) {
      console.error("SSE mini-month refetch error:", err);
    }
  }, [open, selectedDay, miniMonth, getScheduleRange, getMonthSummary, isAdmin]);

  useSSEListener("calendar_update", silentRefetch);

  // Render-time All | Personal | Work filter + all-day split (never a
  // refetch). All-day items feed the strip; only timed items reach the lanes.
  const visibleItems = useMemo(
    () => applyEventFilter(items, eventFilter),
    [items, eventFilter]
  );
  const timedItems = useMemo(
    () => visibleItems.filter((it) => !isAllDayItem(it)),
    [visibleItems]
  );
  const allDayForDay = useMemo(
    () => (selectedDay ? allDayItemsForDay(visibleItems, selectedDay) : []),
    [visibleItems, selectedDay]
  );

  // Auto-scroll to first timed job (or 6 AM default) after load. An all-day
  // noon anchor must not drag the grid to 12p.
  useEffect(() => {
    if (!loading && gridBodyRef.current) {
      let hourOffset = DEFAULT_SCROLL_HOUR - GRID_START_HOUR;
      if (timedItems.length > 0) {
        const firstMs = parseInt(timedItems[0].calendarItem.startMs);
        if (!isNaN(firstMs)) {
          const dt = new Date(firstMs);
          hourOffset =
            dt.getHours() + dt.getMinutes() / 60 - GRID_START_HOUR;
        }
      }
      gridBodyRef.current.scrollTop = Math.max(0, hourOffset * HOUR_HEIGHT - 40);
    }
  }, [loading, timedItems]);

  // Close pickers on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  useEffect(() => {
    if (pickerOpen === "year" && yearPickerRef.current) {
      const active = yearPickerRef.current.querySelector(".active");
      if (active) active.scrollIntoView({ block: "center" });
    }
  }, [pickerOpen]);

  const togglePicker = (type) => {
    setPickerOpen((prev) => (prev === type ? null : type));
  };

  const handlePrevDay = () => {
    setSelectedDay((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      // Keep mini-month aligned so selectedDay is always visible in it.
      if (
        d.getMonth() !== miniMonth.getMonth() ||
        d.getFullYear() !== miniMonth.getFullYear()
      ) {
        setMiniMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      }
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  const handleNextDay = () => {
    setSelectedDay((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      if (
        d.getMonth() !== miniMonth.getMonth() ||
        d.getFullYear() !== miniMonth.getFullYear()
      ) {
        setMiniMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      }
      setCurrentMonth?.(new Date(d.getFullYear(), d.getMonth(), 1));
      return d;
    });
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    setSelectedDay(today);
    setMiniMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setCurrentMonth?.(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const handleMiniPrevMonth = () => {
    setMiniMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleMiniNextMonth = () => {
    setMiniMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleMiniDayClick = (date) => {
    setSelectedDay(startOfDay(date));
    setMiniMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setCurrentMonth?.(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  const handlePickMonth = (monthIdx) => {
    const year = selectedDay
      ? selectedDay.getFullYear()
      : new Date().getFullYear();
    setMiniMonth(new Date(year, monthIdx, 1));
    setPickerOpen(null);
  };

  const handlePickYear = (year) => {
    const month = selectedDay ? selectedDay.getMonth() : new Date().getMonth();
    setMiniMonth(new Date(year, month, 1));
    setPickerOpen(null);
  };

  // Bucket items into the selected day via splitEventForDay — multi-day
  // events get day-clamped start/end and first/last flags for chevrons.
  const dayBlocks = useMemo(() => {
    if (!selectedDay || timedItems.length === 0) return [];
    const dayStartMs = startOfDay(selectedDay).getTime();
    const dayEndMs = endOfDay(selectedDay).getTime();

    const bucket = [];
    for (const item of timedItems) {
      const seg = splitEventForDay(item.calendarItem, dayStartMs, dayEndMs);
      if (!seg) continue;
      bucket.push({
        ...item,
        calendarItem: {
          ...item.calendarItem,
          startMs: seg.segStartMs,
          endMs: seg.segEndMs,
        },
        _originalItem: item,
        _isMultiDayPart: seg.totalWorkDays > 1,
        _isFirstDay: seg.isFirstDay,
        _isLastDay: seg.isLastDay,
        _workDayIndex: seg.workDayIndex,
        _totalWorkDays: seg.totalWorkDays,
      });
    }

    const gridOriginMs = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate(),
      GRID_START_HOUR
    ).getTime();

    return computeLaneLayout(bucket, gridOriginMs, HOUR_HEIGHT, 18);
  }, [timedItems, selectedDay]);

  // Weather lookup: { byZone, fetchedByZone } built once per weather fetch.
  // Joined per block by item.zone + the Central day of the block's startMs.
  const weatherLookup = useMemo(() => buildWeatherLookup(zoneWeather), [zoneWeather]);

  const handleGridMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (gridBodyRef.current?.scrollTop || 0);
    const hourIdx = Math.floor(y / HOUR_HEIGHT);
    setHoveredHour(hourIdx >= 0 && hourIdx < GRID_HOURS ? hourIdx : null);
  };

  const handleGridClick = (e) => {
    if (!selectedDay) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (gridBodyRef.current?.scrollTop || 0);
    const x = e.clientX - rect.left;
    if (x < HOUR_LABEL_WIDTH) return;

    const hourOffset = Math.floor(y / HOUR_HEIGHT);
    const clickedHour = (GRID_START_HOUR + hourOffset) % 24;

    // If the grid offset rolls past midnight (GRID_START_HOUR + hourOffset >= 24),
    // the created event would fall on the next day. Anchor to selectedDay
    // so single-click-to-create never silently jumps forward a day — that's
    // what the multi-day editor is for.
    const start = new Date(
      selectedDay.getFullYear(),
      selectedDay.getMonth(),
      selectedDay.getDate(),
      clickedHour,
      0,
      0
    );
    const end = new Date(start.getTime() + 4 * 3600000);

    setCreateEventStartMs(start.getTime().toString());
    setCreateEventEndMs(end.getTime().toString());
    setCreateEventType("job");
    setCreateEventOpen(true);
  };

  // Header buttons: "Create Event +" seeds a job, "SIX" a personal event;
  // both clear any grid-click prefill.
  const openCreate = (type) => {
    setCreateEventStartMs("");
    setCreateEventEndMs("");
    setCreateEventType(type);
    setCreateEventOpen(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(null);
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleEventUpdated = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    fetchDayData();
    fetchMiniMonthData();
  };

  const handleCreateEventDone = () => {
    setCreateEventOpen(false);
    fetchDayData();
    fetchMiniMonthData();
  };

  // Build mini-month cells (7-col grid with leading/trailing padding).
  const miniCells = useMemo(() => {
    if (!miniMonth) return [];
    const year = miniMonth.getFullYear();
    const month = miniMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();

    // Keyed by LOCAL calendar day, not by an exact ms match: the server
    // buckets each day at CENTRAL NOON (phase PX, 2026-09-20), and the old
    // strict-equality lookup against local midnight silently emptied this
    // mini-month whenever a DST transition sat inside the window. A noon
    // anchor lands on the same calendar day in every US zone.
    const summaryMap = {};
    summaries.forEach((s) => {
      const ms = parseInt(s.dateMs, 10);
      if (Number.isNaN(ms)) return;
      const d = new Date(ms);
      summaryMap[`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`] = s;
    });

    const cells = [];
    const prevMonthLast = new Date(year, month, 0);
    for (let i = startDow - 1; i >= 0; i--) {
      const day = prevMonthLast.getDate() - i;
      cells.push({
        date: new Date(year, month - 1, day),
        otherMonth: true,
      });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dt = new Date(year, month, d);
      cells.push({
        date: dt,
        otherMonth: false,
        summary: summaryMap[`${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`],
      });
    }
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      cells.push({
        date: new Date(year, month + 1, i),
        otherMonth: true,
      });
    }
    return cells;
  }, [miniMonth, summaries]);

  const today = useMemo(() => startOfDay(new Date()), []);

  // Priority notes for the selected day, sorted by event start time.
  // Surfaced in the aside under the mini-month nav so they're visible
  // alongside the timeline (DayModal showed them above the grid; this
  // modal previously omitted them entirely).
  // Personal ("SIX") filter (2026-09-14): derived from the FILTERED list and
  // empty under "Personal" — priority notes belong to jobs, so a personal-
  // only view must not surface them; the panel itself is hidden then.
  const showPriorityNotes = eventFilter !== EVENT_FILTER_PERSONAL;
  const priorityNotes = useMemo(() => {
    const notes = [];
    if (!showPriorityNotes) return notes;
    visibleItems.forEach((item) => {
      const customerFirst = item.customer?.firstName || "";
      const customerLast = item.customer?.lastName || "";
      const customerName =
        `${customerFirst} ${customerLast}`.trim() || "Unknown";
      (item.calendarItem?.priorityNotes || []).forEach((note) => {
        if (!note) return;
        notes.push({
          note,
          customerName,
          startMs: parseInt(item.calendarItem?.startMs) || 0,
        });
      });
    });
    notes.sort((a, b) => a.startMs - b.startMs);
    return notes;
  }, [visibleItems, showPriorityNotes]);

  const getDayLabel = () => {
    if (!selectedDay) return "";
    return selectedDay.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const displayMonth = miniMonth ? miniMonth.getMonth() : new Date().getMonth();
  const displayYear = miniMonth
    ? miniMonth.getFullYear()
    : new Date().getFullYear();

  return (
    <>
      <BdrModal
        open={open}
        onClose={onClose}
        variant="panel"
        maxWidth="1200px"
        showHeader={false}
        ariaLabel="Day schedule"
        panelClassName="cal-tall-panel"
        /* Tagged so a walkthrough won't START over an open calendar in any
           view (tours/tourModals.js). onClose is CalendarPage.handleClose ->
           the navbar's close, state only. */
        tourTag="calendar-day"
      >
        <div className="day-view-header">
          <div className="day-view-header-nav">
            <button className="week-nav-btn" onClick={handlePrevDay}>
              &#8249;
            </button>
            <h3 ref={pickerRef} className="day-view-header-label">
              <button
                className="day-view-today-btn"
                onClick={handleToday}
                type="button"
              >
                Today
              </button>
              <span className="week-header-divider">|</span>
              <span>{getDayLabel()}</span>
              {/* The redundant header month/year picker hides at <900px —
                  the mini-month has its own month nav, so users don't lose
                  the ability to jump months on narrow viewports. */}
              <span className="day-view-header-month-picker">
                <span className="week-header-divider">|</span>
                <span
                  className="calendar-picker-trigger"
                  onClick={() => togglePicker("month")}
                >
                  {MONTH_NAMES[displayMonth]}
                </span>{" "}
                <span
                  className="calendar-picker-trigger"
                  onClick={() => togglePicker("year")}
                >
                  {displayYear}
                </span>

                {pickerOpen === "month" && (
                  <div className="calendar-picker-dropdown calendar-month-picker">
                    {MONTH_NAMES.map((name, i) => (
                      <div
                        key={i}
                        className={`calendar-picker-option${
                          i === displayMonth ? " active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePickMonth(i);
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}

                {pickerOpen === "year" && (
                  <div
                    className="calendar-picker-dropdown calendar-year-picker"
                    ref={yearPickerRef}
                  >
                    {Array.from({ length: 21 }, (_, i) => {
                      const year = displayYear - 10 + i;
                      return (
                        <div
                          key={year}
                          className={`calendar-picker-option${
                            year === displayYear ? " active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickYear(year);
                          }}
                        >
                          {year}
                        </div>
                      );
                    })}
                  </div>
                )}
              </span>
            </h3>
            <button className="week-nav-btn" onClick={handleNextDay}>
              &#8250;
            </button>
          </div>
          <div className="day-view-header-actions">
            {isAdmin && (
              <EventFilterToggle value={eventFilter} onChange={onEventFilterChange} />
            )}
            <button
              className={`calendar-week-btn${showAIPanel ? " active-ai" : ""}`}
              onClick={() => setShowAIPanel(!showAIPanel)}
            >
              {showAIPanel ? "Close AI" : "AI Assign"}
            </button>
            <button className="calendar-week-btn" onClick={onSwitchToWeek}>
              View Week
            </button>
            <button className="calendar-week-btn" onClick={onSwitchToMonth}>
              View Month
            </button>
            <button
              className="calendar-create-btn bdr-grad-cal-green"
              onClick={() => openCreate("job")}
            >
              Create Event +
            </button>
            {isAdmin && (
              <button
                className="calendar-create-btn bdr-grad-cal-green calendar-six-btn"
                title="Personal event"
                onClick={() => openCreate("personal")}
              >
                SIX
                <HearingIcon sx={{ fontSize: 16 }} />
              </button>
            )}
            <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {showAIPanel && selectedDay && (
          <AIAssignmentPanel
            startMs={startOfDay(selectedDay).getTime().toString()}
            endMs={endOfDay(selectedDay).getTime().toString()}
            showDatePickers={false}
            onClose={() => setShowAIPanel(false)}
          />
        )}

        <div className="cal-modal-body">
          <div className="day-view-body">
            {/* LEFT: mini-month navigator */}
            <aside className="day-view-mini">
              <div className="day-view-mini-header">
                <button
                  className="day-view-mini-nav"
                  onClick={handleMiniPrevMonth}
                  aria-label="Previous month"
                >
                  &#8249;
                </button>
                <span className="day-view-mini-title">
                  {MONTH_NAMES[displayMonth]} {displayYear}
                </span>
                <button
                  className="day-view-mini-nav"
                  onClick={handleMiniNextMonth}
                  aria-label="Next month"
                >
                  &#8250;
                </button>
              </div>
              <div className="day-view-mini-grid">
                {DAY_NAMES_SHORT.map((name) => (
                  <div key={name} className="day-view-mini-dow">
                    {name[0]}
                  </div>
                ))}
                {miniCells.map((cell, idx) => {
                  const isToday = sameDay(cell.date, today);
                  const isSelected =
                    selectedDay && sameDay(cell.date, selectedDay);
                  // Dots re-derived per filter (personal colours live in
                  // personalUserColors, never in userColors).
                  const userColors = cell.summary
                    ? mergeSummaryForFilter(cell.summary, eventFilter).dots
                    : [];
                  return (
                    <button
                      key={idx}
                      type="button"
                      className={`day-view-mini-cell${
                        cell.otherMonth ? " other-month" : ""
                      }${isToday ? " today" : ""}${
                        isSelected ? " selected" : ""
                      }`}
                      onClick={() => handleMiniDayClick(cell.date)}
                    >
                      <span className="day-view-mini-num">
                        {cell.date.getDate()}
                      </span>
                      {userColors.length > 0 && (
                        <span className="day-view-mini-dots">
                          {userColors.slice(0, 3).map((c, i) => (
                            <span
                              key={i}
                              className="day-view-mini-dot"
                              style={{ backgroundColor: c || "#9e9e9e" }}
                            />
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Priority notes for the currently selected day. Fills the
                  empty space below the mini-month so the aside stays
                  information-dense; scrolls internally when long. Hidden
                  while loading or when the day has no priority notes, and
                  not rendered at all under the "Personal" filter. */}
              {showPriorityNotes && (
              <div className="day-view-priority-panel">
                <div className="day-view-priority-title">Priority Notes</div>
                <div className="day-view-priority-scroll">
                  {loading ? (
                    <div className="day-view-priority-empty">Loading…</div>
                  ) : priorityNotes.length === 0 ? (
                    <div className="day-view-priority-empty">
                      No priority notes
                    </div>
                  ) : (
                    priorityNotes.map((pn, idx) => (
                      <div key={idx} className="day-view-priority-note">
                        <PriorityHighIcon
                          className="day-view-priority-icon"
                          sx={{ fontSize: 14 }}
                        />
                        <div className="day-view-priority-body">
                          <div className="day-view-priority-text">{pn.note}</div>
                          <div className="day-view-priority-customer">
                            {pn.customerName}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              )}
            </aside>

            {/* RIGHT: single-day time grid (one column of WeekModal) */}
            <div className="day-view-grid-wrap">
              {loading ? (
                <div className="week-modal-loading">
                  <AdminBDRSpinner message="Loading..." />
                </div>
              ) : error ? (
                <div className="week-modal-error">{error}</div>
              ) : (
                <>
                  <div className="week-grid-header day-view-single-header">
                    <div
                      className="week-grid-hour-spacer"
                      style={{ minWidth: HOUR_LABEL_WIDTH }}
                    />
                    <div
                      className={`week-grid-day-header${
                        selectedDay && sameDay(selectedDay, today)
                          ? " today"
                          : ""
                      }`}
                    >
                      {selectedDay &&
                        `${DAY_NAMES_SHORT[selectedDay.getDay()]} ${selectedDay.getDate()}`}
                    </div>
                  </div>
                  {/* All-day (personal "SIX") chips — renders nothing when empty */}
                  <AllDayEventStrip
                    days={[allDayForDay]}
                    hourLabelWidth={HOUR_LABEL_WIDTH}
                    colorFor={personalEventColor}
                    onItemClick={(it) =>
                      setSelectedItem((prev) => (prev === it ? null : it))
                    }
                    single
                  />
                  <div
                    className="week-grid-scroll day-view-grid-scroll"
                    ref={gridBodyRef}
                  >
                    <div
                      className="week-grid-body"
                      style={{
                        height: TOTAL_GRID_HEIGHT,
                        position: "relative",
                      }}
                      onClick={handleGridClick}
                      onMouseMove={handleGridMouseMove}
                      onMouseLeave={() => setHoveredHour(null)}
                    >
                      {Array.from({ length: GRID_HOURS }, (_, h) => (
                        <React.Fragment key={h}>
                          <div
                            className="week-grid-hour-line"
                            style={{ top: h * HOUR_HEIGHT }}
                          />
                          <div
                            className={`week-grid-hour-label${
                              hoveredHour === h ? " hovered" : ""
                            }`}
                            style={{
                              top: h * HOUR_HEIGHT - 6,
                              width: HOUR_LABEL_WIDTH - 4,
                            }}
                          >
                            {formatHourLabel((GRID_START_HOUR + h) % 24)}
                          </div>
                        </React.Fragment>
                      ))}

                      {dayBlocks.map((block, idx) => {
                        // Single column: the event spans the full day width
                        // minus an 8% right gutter for click-to-create, and
                        // still stacks via the N+1 virtual lane model so
                        // overlapping events peek out the right edge like
                        // WeekModal does within a single day column.
                        //
                        // left/width percentages are taken against the full
                        // parent width and then back out a proportional share
                        // of HOUR_LABEL_WIDTH — without that subtraction the
                        // block's right edge overshoots the parent at narrow
                        // widths and introduces a horizontal scrollbar. Same
                        // math WeekModal uses for its per-day blocks.
                        const usablePct = 92;
                        const lanePct = usablePct / block.totalVirtualLanes;
                        const leftPct = block.overlapIndex * lanePct;
                        const widthPct = 2 * lanePct;

                        const isGradient =
                          block.background.startsWith("linear-gradient");
                        const hasBanner =
                          (block.item._isMultiDayPart || block.isChained) &&
                          block.heightPx >= 16;
                        const hoverStyle = hoverStyleFor(block);
                        // Weather chip: join this block to its zone's forecast
                        // for the Central day of its (day-clamped) startMs. No
                        // zone or no data -> weatherForJob returns day:null ->
                        // chip renders nothing. Same field paths WeekModal uses
                        // (block.item.zone + block.item.calendarItem.startMs).
                        const { day: weatherDay, fetchedMs: weatherFetchedMs } =
                          weatherForJob(
                            weatherLookup,
                            block.item.zone,
                            block.item.calendarItem.startMs
                          );

                        return (
                          <div
                            key={idx}
                            className={`week-grid-block${
                              block.isCompleted ? " completed" : ""
                            }${
                              hasBanner ? " has-multiday-banner" : ""
                            }${block.isTentative ? " tentative-shimmer" : ""}${
                              block.eventType === "vacation" ? " vacation-stripes" : ""
                            }`}
                            style={{
                              top: block.topPx,
                              height: block.heightPx,
                              left: `calc(${HOUR_LABEL_WIDTH}px + ${leftPct}% - ${(leftPct / 100) * HOUR_LABEL_WIDTH}px)`,
                              width: `calc(${widthPct}% - ${(widthPct / 100) * HOUR_LABEL_WIDTH}px)`,
                              background: isGradient
                                ? block.background
                                : undefined,
                              backgroundColor: !isGradient
                                ? block.background
                                : undefined,
                              zIndex: hoverStyle.zIndex ?? block.overlapIndex + 1,
                              opacity: hoverStyle.opacity,
                              transition: hoverStyle.transition,
                            }}
                            onMouseEnter={() => onHoverEnter(block)}
                            onMouseLeave={onHoverLeave}
                            onClick={(e) => {
                              e.stopPropagation();
                              const popupItem =
                                block.item._originalItem || block.item;
                              setSelectedItem(
                                selectedItem === popupItem ? null : popupItem
                              );
                            }}
                          >
                            <div className="week-grid-block-stripe" />
                            {hasBanner && (
                              <MultiDayBanner
                                isFirstDay={block.item._isFirstDay}
                                isLastDay={block.item._isLastDay}
                                workDayIndex={block.item._workDayIndex}
                                totalWorkDays={block.item._totalWorkDays}
                                isChained={block.isChained}
                                eventType={block.eventType}
                                variant="week"
                              />
                            )}
                            <span className="week-grid-block-name">
                              {getBlockLabelPrefix(block.item.calendarItem)}
                              {getBlockLabelName(block.item)}
                            </span>
                            {block.heightPx > 22 && (
                              <span className="week-grid-block-time">
                                {BAGroupTimeFormat(
                                  block.item.calendarItem.startMs,
                                  true,
                                  false
                                )}
                              </span>
                            )}
                            {block.heightPx > 36 &&
                              getJobAmountStr(block.item) && (
                                <span className="week-grid-block-amount">
                                  {getJobAmountStr(block.item)}
                                </span>
                              )}
                            {block.heightPx > 36 &&
                              getCalendarJobCategory(block.item) && (
                                <span className="week-grid-block-jobtype">
                                  {getCalendarJobCategory(block.item)}
                                </span>
                              )}
                            {block.heightPx > 28 &&
                              getFinisherRange(block.item) && (
                                <span className="week-grid-block-finishers">
                                  {getFinisherRange(block.item)}
                                </span>
                              )}
                            {weatherDay && (
                              <WeatherChip
                                day={weatherDay}
                                fetchedMs={weatherFetchedMs}
                                iconOnly={block.heightPx <= 36}
                                size={14}
                                className="week-grid-block-weather"
                              />
                            )}
                            {block.isCompleted && (
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 10,
                                  color: "#00e676",
                                  position: "absolute",
                                  top: 1,
                                  right: 1,
                                  zIndex: 3,
                                }}
                              />
                            )}
                            {block.isPending && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const invId =
                                    block.item?.calendarItem?.invoiceId;
                                  if (invId) {
                                    setReviewInvoiceId(invId);
                                    setReviewModalOpen(true);
                                  }
                                }}
                                style={{
                                  position: "absolute",
                                  top: 1,
                                  right: 1,
                                  zIndex: 3,
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  backgroundColor: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <ScheduleIcon
                                  sx={{ fontSize: 10, color: "#FF9800" }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Current-time line + pill — visible only when the
                          selected day is today; dims to 0.4 alpha while a
                          block is hovered (hover spotlight stays dominant). */}
                      <CurrentTimeLine
                        hourHeight={HOUR_HEIGHT}
                        gridStartHour={GRID_START_HOUR}
                        gridHours={GRID_HOURS}
                        hourLabelWidth={HOUR_LABEL_WIDTH}
                        visible={selectedDay ? sameDay(selectedDay, new Date()) : false}
                        dimmed={isHoverActive}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </BdrModal>

      {/* Detail popup — shared across all calendar surfaces */}
      <EventDetailPopup
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onEdit={handleEditClick}
        onViewInvoice={setViewInvoice}
        onViewQuote={setViewQuote}
      />

      <EditEventModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingItem(null);
        }}
        onEventUpdated={handleEventUpdated}
        item={editingItem}
      />

      {viewInvoice &&
        createPortal(
          <ViewClientInvoiceModal
            selectedInvoice={viewInvoice}
            closeInvoiceModal={() => setViewInvoice(null)}
            queryClient={null}
            emailObjHandler={() => {}}
            changeParentForEmailModal={() => {}}
          />,
          document.body
        )}

      {viewQuote &&
        createPortal(
          <ViewClientQuoteModal
            selectedQuote={viewQuote}
            closeQuoteModal={() => setViewQuote(null)}
            queryClient={null}
            emailObjHandler={() => {}}
            changeParentForEmailModal={() => {}}
          />,
          document.body
        )}

      <CreateEventModal
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        onEventCreated={handleCreateEventDone}
        passedStartMs={createEventStartMs}
        passedEndMs={createEventEndMs}
        initialEventType={createEventType}
      />

      <JobFormReviewModal
        open={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setReviewInvoiceId(null);
        }}
        invoiceId={reviewInvoiceId}
        onStatusChanged={() => {
          setReviewModalOpen(false);
          setReviewInvoiceId(null);
          fetchDayData();
        }}
      />
    </>
  );
};

export default DayViewModal;
